/* ------------------------------------------------------------------ *
 * Self-contained shims — replace the Horizon @theme/* modules so the
 * x-cart runs on any theme (here: "Le Grand Rayon"). No build step.
 * ------------------------------------------------------------------ */

/** Internal cart events. CartAdd opens the drawer; CartUpdate only re-renders. */
const ThemeEvents = { cartUpdate: 'x-cart:update' };
const CartAddEvent = { eventName: 'x-cart:add' };

/** Locale-aware cart routes via the universal Shopify global. */
function xRouteBase() {
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  return root.replace(/\/$/, '');
}
const XRoutes = {
  get cart_url() { return xRouteBase() + '/cart'; },
  get cart_add_url() { return xRouteBase() + '/cart/add'; },
  get cart_change_url() { return xRouteBase() + '/cart/change'; },
  get cart_update_url() { return xRouteBase() + '/cart/update'; },
};

/** Build a fetch() init for cart AJAX. Marks the request as x-cart-internal. */
function fetchConfig(type = 'json', config = {}) {
  return {
    method: 'POST',
    headers: {
      'Content-Type': `application/${type}`,
      Accept: `application/${type}`,
      'X-Requested-With': 'XMLHttpRequest',
      'X-XCart-Internal': '1',
    },
    ...config,
  };
}

/**
 * Replace a section's inner DOM from Section Rendering API HTML.
 * The API returns the section's rendered content (what lives INSIDE
 * `#shopify-section-{id}`). If the payload happens to include the wrapper
 * div, unwrap it; otherwise the payload IS the inner content.
 */
function morphSection(sid, html) {
  const target = document.getElementById(`shopify-section-${sid}`);
  if (!target || typeof html !== 'string') return;
  // Preserve the drawer's open state across the content swap: innerHTML
  // replacement rebuilds a fresh (closed) drawer, so we re-apply the open
  // attributes instantly (no animation) when it was open before.
  const wasOpen = !!target.querySelector('[data-x-cart-panel][data-open]');
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const wrapper = doc.getElementById(`shopify-section-${sid}`);
  target.innerHTML = wrapper ? wrapper.innerHTML : html;
  if (wasOpen) {
    const root = target.querySelector('x-cart-root[data-presentation="drawer"]');
    const backdrop = root && root.querySelector('[data-x-cart-backdrop]');
    const panel = root && root.querySelector('[data-x-cart-panel]');
    if (root instanceof HTMLElement && backdrop instanceof HTMLElement && panel instanceof HTMLElement) {
      root.setAttribute('data-x-drawer-open', '');
      backdrop.hidden = false;
      panel.hidden = false;
      panel.setAttribute('aria-hidden', 'false');
      backdrop.setAttribute('data-open', '');
      panel.setAttribute('data-open', '');
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
    }
  }
}

/** Minimal morph used only by the upsell quick-add modal. */
function morph(target, source) {
  if (target && source) target.replaceChildren(...source.cloneNode(true).childNodes);
}

/** CartUpdateEvent shim: a CustomEvent carrying the cart JSON in detail.data. */
class CartUpdateEvent extends CustomEvent {
  /**
   * @param {Record<string, unknown>} data
   * @param {string} sourceId
   * @param {Record<string, unknown>} [extra]
   */
  constructor(data, sourceId, extra = {}) {
    super(ThemeEvents.cartUpdate, { bubbles: true, detail: { data, sourceId, ...extra } });
  }
}

/**
 * Verbose cart refresh tracing. Filter DevTools console by `[x-cart]`.
 * Off by default in production — enable: `sessionStorage.setItem('x_cart_debug','1')`.
 */
function xCartDebugEnabled() {
  try {
    return sessionStorage.getItem('x_cart_debug') === '1';
  } catch {
    return false;
  }
}

/**
 * @param {string} phase
 * @param {Record<string, unknown>} [data]
 */
function dclog(phase, data) {
  if (!xCartDebugEnabled()) return;
  console.info('[x-cart]', phase, data ?? {});
}

/**
 * @param {Record<string, string | null | undefined> | undefined} sections
 */
function sectionKeysSummary(sections) {
  if (!sections || typeof sections !== 'object') return { keys: [], nullish: [] };
  const keys = Object.keys(sections);
  const nullish = keys.filter((k) => sections[k] == null || sections[k] === '');
  return { keys, nullish };
}

/**
 * @param {Response} res
 * @returns {Promise<Record<string, unknown> | null>}
 */
async function readCartJsonResponse(res, logLabel = 'cart') {
  const ct = res.headers.get('content-type') || '';
  const text = await res.text();
  if (!res.ok) {
    dclog('readCartJson: http error', {
      label: logLabel,
      status: res.status,
      contentType: ct,
      bodyPreview: text.slice(0, 400),
    });
    console.warn('x-cart HTTP', res.status, text.slice(0, 240));
    return null;
  }
  if (!ct.includes('json') && !text.trimStart().startsWith('{')) {
    dclog('readCartJson: not json', {
      label: logLabel,
      contentType: ct,
      bodyPreview: text.slice(0, 400),
    });
    console.warn('x-cart: expected JSON body');
    return null;
  }
  try {
    const parsed = /** @type {Record<string, unknown>} */ (JSON.parse(text));
    dclog('readCartJson: ok', {
      label: logLabel,
      item_count: parsed.item_count,
      ...sectionKeysSummary(
        /** @type {Record<string, string | null | undefined>} */ (parsed.sections)
      ),
    });
    return parsed;
  } catch {
    dclog('readCartJson: parse error', { label: logLabel, contentType: ct, bodyPreview: text.slice(0, 200) });
    console.warn('x-cart: invalid JSON');
    return null;
  }
}

/** Max section IDs per Section Rendering API request (Shopify limit). */
const X_SECTION_RENDER_BATCH = 5;

// No cart cap on this store (the Ummahya scarcity limit is removed). Kept as a
// constant so the upsell-add guard below compiles; Infinity = never blocks.
const CART_LIMIT_CENTS = Infinity;

/**
 * `sections_url` for bundled cart + section rendering — include search so pagination/filters match.
 * @returns {string}
 */
function xCartSectionsUrl() {
  return window.location.pathname + window.location.search;
}

/**
 * Collect all x cart section IDs (drawer + page + placeholders) for Cart API `sections` param.
 * @returns {string[]}
 */
function getXSectionIds() {
  /** @type {string[]} */
  const ids = [];
  for (const el of document.querySelectorAll('[data-x-cart-section-id]')) {
    if (!(el instanceof HTMLElement)) continue;
    if (el.dataset.xSkip === 'true') continue;
    const id = el.dataset.xCartSectionId;
    if (id && !ids.includes(id)) ids.push(id);
  }
  return ids;
}

/**
 * Gift rules from each `x-cart-root` (merged; duplicate variant+threshold keys deduped).
 * @returns {{ variantId: number; thresholdCents: number; available: boolean }[]}
 */
function getXGiftRules() {
  /** @type {Map<string, { variantId: number; thresholdCents: number; available: boolean }>} */
  const byKey = new Map();
  for (const root of document.querySelectorAll('x-cart-root')) {
    const script = root.querySelector('script[type="application/json"][data-x-gift-rules]');
    if (!script?.textContent) continue;
    try {
      const parsed = JSON.parse(script.textContent.trim());
      if (!Array.isArray(parsed)) continue;
      for (const rule of parsed) {
        const vid = Number(rule.variantId);
        const th = Number(rule.thresholdCents) || 0;
        if (!vid || th < 1) continue;
        const key = `${vid}-${th}`;
        byKey.set(key, {
          variantId: vid,
          thresholdCents: th,
          available: rule.available !== false,
        });
      }
    } catch {
      /* ignore malformed JSON */
    }
  }
  return [...byKey.values()];
}

/**
 * @returns {Promise<Record<string, unknown> | null>}
 */
async function fetchXCartJs() {
  try {
    const base = XRoutes.cart_url.replace(/\/$/, '');
    const res = await fetch(`${base}.js`, { headers: { Accept: 'application/json' } });
    if (!res.ok) return null;
    return /** @type {Record<string, unknown>} */ (await res.json());
  } catch {
    return null;
  }
}

/**
 * @param {Record<string, unknown>} item
 */
function isXGiftCartItem(item) {
  const props = item?.properties;
  if (!props || typeof props !== 'object') return false;
  const rec = /** @type {Record<string, unknown>} */ (props);
  return rec._x_gift === '1' || rec['_x_gift'] === '1';
}

/**
 * @param {Record<string, unknown>} data
 */
function dispatchXGiftCartUpdate(data) {
  document.dispatchEvent(
    new CartUpdateEvent(data, 'x-cart-gift-sync', {
      itemCount: /** @type {number} */ (data.item_count ?? 0),
      source: 'x-cart-gift-sync',
      sections: /** @type {Record<string, string>} */ (data.sections),
    })
  );
}

/**
 * @param {number} line
 * @param {number} quantity
 * @param {string[]} ids
 */
async function xGiftApplyChangeLine(line, quantity, ids) {
  const body = JSON.stringify({
    line,
    quantity,
    sections: ids.join(','),
    sections_url: xCartSectionsUrl(),
  });
  const res = await fetch(XRoutes.cart_change_url, {
    ...fetchConfig('json', { body }),
  });
  const data = await readCartJsonResponse(res, 'giftSync:changeLine');
  if (!data || data.errors) return;
  dispatchXGiftCartUpdate(data);
}

/**
 * @param {number} variantId
 * @param {string[]} ids
 */
async function xGiftApplyAdd(variantId, ids) {
  const formData = new FormData();
  formData.append('id', String(variantId));
  formData.append('quantity', '1');
  formData.append('properties[_x_gift]', '1');
  formData.append('sections', ids.join(','));
  formData.append('sections_url', xCartSectionsUrl());
  const res = await fetch(XRoutes.cart_add_url, {
    method: 'POST',
    headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest', 'X-XCart-Internal': '1' },
    body: formData,
  });
  const data = await readCartJsonResponse(res, 'giftSync:add');
  if (!data || data.status) return;
  dispatchXGiftCartUpdate(data);
}

let xGiftSyncTimer = /** @type {ReturnType<typeof setTimeout> | null} */ (null);
let xGiftSyncInFlight = false;

function scheduleXGiftSync() {
  if (xGiftSyncTimer) clearTimeout(xGiftSyncTimer);
  xGiftSyncTimer = setTimeout(() => {
    xGiftSyncTimer = null;
    void runXGiftSync();
  }, 48);
}

async function runXGiftSync() {
  const rules = getXGiftRules();
  if (rules.length === 0) return;
  const ids = getXSectionIds();
  if (ids.length === 0) return;
  if (xGiftSyncInFlight) {
    scheduleXGiftSync();
    return;
  }

  xGiftSyncInFlight = true;
  try {
    let cart = await fetchXCartJs();
    if (!cart || !Array.isArray(cart.items)) return;

    /** @type {Set<number>} */
    const variantIds = new Set(rules.map((r) => r.variantId));

    for (const vid of variantIds) {
      const subtotal = /** @type {number} */ (cart.items_subtotal_price ?? 0);
      const rulesForVariant = rules.filter((r) => r.variantId === vid);
      const shouldHave = rulesForVariant.some(
        (r) => subtotal >= r.thresholdCents && r.available
      );

      const idx = cart.items.findIndex(
        (item) =>
          item.variant_id === vid && isXGiftCartItem(/** @type {Record<string, unknown>} */ (item))
      );
      const hasGiftLine = idx >= 0;
      const lineNum = idx + 1;

      if (!shouldHave && hasGiftLine) {
        await xGiftApplyChangeLine(lineNum, 0, ids);
        cart = await fetchXCartJs();
        if (!cart || !Array.isArray(cart.items)) return;
        continue;
      }

      if (shouldHave && !hasGiftLine) {
        await xGiftApplyAdd(vid, ids);
        cart = await fetchXCartJs();
        if (!cart || !Array.isArray(cart.items)) return;
      }
    }
  } finally {
    xGiftSyncInFlight = false;
  }
}

/**
 * Morph every x section present in a Cart API `sections` map (e.g. after add to cart).
 * @param {Record<string, string | null | undefined> | undefined} sections
 */
function morphXSectionsFromResponse(sections) {
  if (!sections || typeof sections !== 'object') {
    dclog('morph: skip (no sections object)', {});
    return;
  }
  const summary = sectionKeysSummary(sections);
  dclog('morph: start', { responseKeys: summary.keys, responseNullishKeys: summary.nullish });
  // Iterate the RESPONSE keys (each is a section id whose `#shopify-section-{id}`
  // wrapper lives in the DOM). More robust than matching against DOM dataset ids,
  // which can desync from the group-prefixed id Shopify returns.
  for (const sid of Object.keys(sections)) {
    const html = sections[sid];
    const hasDomRoot = !!document.getElementById(`shopify-section-${sid}`);
    if (html && hasDomRoot) {
      try {
        morphSection(sid, html);
        dclog('morph: ok', { sid, htmlLength: String(html).length });
      } catch (err) {
        dclog('morph: threw', { sid, error: String(err) });
        console.warn('x-cart morph', sid, err);
      }
    } else {
      dclog('morph: skip key', { sid, hasHtml: !!html, hasDomRoot });
    }
  }
}

/**
 * Re-render the x cart sections via the Cart Ajax API.
 *
 * NB: the standalone Section Rendering API (`?sections=<group-section-id>`) is
 * unreliable for sections that live inside a section GROUP — Shopify can key the
 * response by the page's main section ("product") instead of the cart section.
 * The Cart Ajax `sections` param renders cart sections in the correct (cart)
 * context and keys them by the requested id, so we use a no-op /cart/update.js
 * call to fetch fresh markup. Marked internal so the fetch bridge ignores it.
 */
async function fetchXSectionsViaRenderingApi() {
  const ids = getXSectionIds();
  if (ids.length === 0) {
    dclog('refresh sections: no dom ids', {});
    return;
  }
  dclog('refresh sections: start', { ids });
  try {
    const res = await fetch(XRoutes.cart_update_url + '.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'X-XCart-Internal': '1',
      },
      body: JSON.stringify({
        sections: ids.join(','),
        sections_url: window.location.pathname + window.location.search,
      }),
    });
    if (!res.ok) {
      dclog('refresh sections: http error', { status: res.status });
      return;
    }
    const data = await readCartJsonResponse(res, 'refreshSections');
    if (data && data.sections) {
      morphXSectionsFromResponse(/** @type {Record<string, string>} */ (data.sections));
    }
  } catch (err) {
    dclog('refresh sections: threw', { error: String(err) });
  }
}

/**
 * @param {Record<string, string | null> | undefined} sections
 * @returns {boolean}
 */
function xSectionsResponseIncomplete(sections) {
  const ids = getXSectionIds();
  if (ids.length === 0) return false;
  if (!sections || typeof sections !== 'object') return true;
  return ids.some((id) => {
    const html = sections[id];
    return html == null || html === '';
  });
}

/**
 * Theme cart events use `Event` subclasses (`CartAddEvent`, `CartUpdateEvent`) with a `detail` bag — not `CustomEvent`.
 * @param {Event} e
 * @returns {Record<string, unknown> | undefined}
 */
function getCartEventData(e) {
  if (!('detail' in e) || !e.detail || typeof e.detail !== 'object') return undefined;
  const data = /** @type {{ data?: Record<string, unknown> }} */ (e.detail).data;
  return data && typeof data === 'object' ? data : undefined;
}

let xMorphListenerAttached = false;
if (!xMorphListenerAttached) {
  xMorphListenerAttached = true;
  document.addEventListener(ThemeEvents.cartUpdate, async (e) => {
    if (!(e instanceof Event)) return;
    const data = getCartEventData(e);
    const sections = /** @type {Record<string, string | null> | undefined} */ (data?.sections);
    const incomplete = xSectionsResponseIncomplete(sections);
    const domIds = getXSectionIds();
    dclog('cart:update listener', {
      source: data?.source,
      itemCount: data?.itemCount,
      domIds,
      incomplete,
      ...sectionKeysSummary(sections),
    });
    morphXSectionsFromResponse(sections);

    if (incomplete) {
      try {
        await fetchXSectionsViaRenderingApi();
      } catch (err) {
        dclog('cart:update fallback threw', { error: String(err) });
        console.warn('x-cart: section rendering fallback failed', err);
      }
    }

    scheduleXGiftSync();
  });
}

class XCartRoot extends HTMLElement {
  /** @type {AbortController | null} */
  #abort = null;

  /** @type {Element | null} */
  #focusBeforeOpen = null;

  /** @type {number} */
  #busyDepth = 0;

  connectedCallback() {
    this.#presentation = this.dataset.presentation === 'page' ? 'page' : 'drawer';
    this.#sectionId = this.dataset.xCartSectionId || '';
    this.#autoOpen = this.dataset.autoOpen === 'true';

    if (this.#presentation === 'drawer') {
      document.addEventListener(CartAddEvent.eventName, this.#onCartAdd);
      document.addEventListener('click', this.#onDocumentClick, true);
      document.addEventListener('keydown', this.#onKeydown);
    }

    this.addEventListener('click', this.#onRootClick);

    scheduleXGiftSync();
  }

  disconnectedCallback() {
    document.removeEventListener(CartAddEvent.eventName, this.#onCartAdd);
    document.removeEventListener('click', this.#onDocumentClick, true);
    document.removeEventListener('keydown', this.#onKeydown);
    this.removeEventListener('click', this.#onRootClick);
    this.#unlockScroll();
  }

  /** @type {'drawer' | 'page'} */
  #presentation = 'drawer';

  /** @type {string} */
  #sectionId = '';

  /** @type {boolean} */
  #autoOpen = false;

  /** @type {AbortController | null} */
  #pickAbort = null;

  get #isOpen() {
    const panel = this.querySelector('[data-x-cart-panel]');
    return panel instanceof HTMLElement && panel.hasAttribute('data-open');
  }

  get #isBusy() {
    return this.#busyDepth > 0;
  }

  #pushBusy() {
    this.#busyDepth += 1;
    this.dataset.xBusy = 'true';
  }

  #popBusy() {
    this.#busyDepth = Math.max(0, this.#busyDepth - 1);
    if (this.#busyDepth === 0) {
      delete this.dataset.xBusy;
    }
  }

  #lockScroll() {
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
  }

  #unlockScroll() {
    document.documentElement.style.overflow = '';
    document.body.style.overflow = '';
  }

  open() {
    if (this.#presentation !== 'drawer') return;
    const backdrop = this.querySelector('[data-x-cart-backdrop]');
    const panel = this.querySelector('[data-x-cart-panel]');
    if (!(backdrop instanceof HTMLElement) || !(panel instanceof HTMLElement)) return;

    this.#focusBeforeOpen = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    this.setAttribute('data-x-drawer-open', '');

    backdrop.hidden = false;
    panel.hidden = false;
    panel.setAttribute('aria-hidden', 'false');

    requestAnimationFrame(() => {
      backdrop.setAttribute('data-open', '');
      panel.setAttribute('data-open', '');
      requestAnimationFrame(() => {
        const closeBtn = this.querySelector('[data-x-cart-close]');
        if (closeBtn instanceof HTMLElement) closeBtn.focus({ preventScroll: true });
      });
    });
    this.#lockScroll();
  }

  close() {
    if (this.#presentation !== 'drawer') return;
    const backdrop = this.querySelector('[data-x-cart-backdrop]');
    const panel = this.querySelector('[data-x-cart-panel]');
    if (!(backdrop instanceof HTMLElement) || !(panel instanceof HTMLElement)) return;

    backdrop.removeAttribute('data-open');
    panel.removeAttribute('data-open');
    panel.setAttribute('aria-hidden', 'true');

    const done = () => {
      backdrop.hidden = true;
      panel.hidden = true;
      backdrop.removeEventListener('transitionend', done);
      this.removeAttribute('data-x-drawer-open');
    };
    backdrop.addEventListener('transitionend', done, { once: true });
    setTimeout(done, 350);
    this.#unlockScroll();

    const prev = this.#focusBeforeOpen;
    this.#focusBeforeOpen = null;
    if (prev instanceof HTMLElement && document.contains(prev)) {
      requestAnimationFrame(() => prev.focus({ preventScroll: true }));
    }
  }

  /** @param {Event} e */
  #onCartAdd = (e) => {
    if (!this.#autoOpen || this.#presentation !== 'drawer') return;
    if (getCartEventData(e)?.didError) return;
    this.open();
  };

  /** @param {KeyboardEvent} e */
  #onKeydown = (e) => {
    if (e.key !== 'Escape' || !this.#isOpen) return;
    e.preventDefault();
    this.close();
  };

  /** @param {MouseEvent} e */
  #onDocumentClick = (e) => {
    if (!(e.target instanceof Element)) return;
    const trigger = e.target.closest('[data-x-cart-open]');
    if (trigger) {
      e.preventDefault();
      this.open();
    }
  };

  /** @param {MouseEvent} e */
  #onRootClick = (e) => {
    if (!(e.target instanceof Element)) return;

    if (e.target.closest('[data-x-cart-close]') || e.target.closest('[data-x-cart-backdrop]')) {
      e.preventDefault();
      this.close();
      return;
    }

    const pick = e.target.closest('[data-x-upsell-pick]');
    if (pick instanceof HTMLElement) {
      const productUrl = pick.dataset.productUrl;
      const productTitle = pick.dataset.productTitle || '';
      if (productUrl) void this.#openProductQuickAdd(productUrl, productTitle);
      return;
    }

    if (this.#isBusy) return;

    const dec = e.target.closest('[data-x-qty-dec]');
    const inc = e.target.closest('[data-x-qty-inc]');
    const remove = e.target.closest('[data-x-remove]');
    const upsell = e.target.closest('[data-x-upsell-add]');
    const promoToggle = e.target.closest('[data-x-promo-toggle]');
    const promoApply = e.target.closest('[data-x-promo-apply]');

    if (dec instanceof HTMLElement) {
      const line = parseInt(dec.dataset.line || '0', 10);
      const row = this.querySelector(`[data-x-line-row="${line}"]`);
      const qtyEl = row?.querySelector(`[data-line-qty="${line}"]`);
      const q = parseInt(qtyEl?.textContent || '1', 10);
      if (line) this.#changeLine(line, Math.max(0, q - 1));
      return;
    }

    if (inc instanceof HTMLElement) {
      const line = parseInt(inc.dataset.line || '0', 10);
      const row = this.querySelector(`[data-x-line-row="${line}"]`);
      const qtyEl = row?.querySelector(`[data-line-qty="${line}"]`);
      const q = parseInt(qtyEl?.textContent || '1', 10);
      if (line) this.#changeLine(line, q + 1);
      return;
    }

    if (remove instanceof HTMLElement) {
      const line = parseInt(remove.dataset.line || '0', 10);
      if (line) this.#changeLine(line, 0);
      return;
    }

    if (upsell instanceof HTMLElement) {
      const vid = upsell.dataset.variantId;
      if (vid) this.#addVariant(vid, upsell);
      return;
    }


    if (promoToggle instanceof HTMLElement) {
      const wrap = this.querySelector('[data-x-promo-toggle-wrap]');
      const form = this.querySelector('[data-x-promo-form]');
      wrap?.classList.add('hidden');
      form?.classList.remove('hidden');
      const input = this.querySelector('[data-x-promo-input]');
      if (input instanceof HTMLInputElement) input.focus();
      return;
    }

    if (promoApply instanceof HTMLElement) {
      const input = this.querySelector('[data-x-promo-input]');
      const code = input instanceof HTMLInputElement ? input.value.trim() : '';
      if (code) this.#applyDiscount(code, promoApply);
      return;
    }
  };

  /**
   * @param {number} line
   * @param {number} quantity
   */
  async #changeLine(line, quantity) {
    const ids = getXSectionIds();
    if (ids.length === 0) return;

    this.#abort?.abort();
    this.#abort = new AbortController();

    this.#pushBusy();
    const body = JSON.stringify({
      line,
      quantity,
      sections: ids.join(','),
      sections_url: xCartSectionsUrl(),
    });

    try {
      dclog('changeLine: request', {
        url: XRoutes.cart_change_url,
        line,
        quantity,
        sectionsRequested: ids,
        sections_url: xCartSectionsUrl(),
      });
      const res = await fetch(XRoutes.cart_change_url, {
        ...fetchConfig('json', { body }),
        signal: this.#abort.signal,
      });
      const data = await readCartJsonResponse(res, 'changeLine');
      if (!data) return;
      if (data.errors) {
        dclog('changeLine: cart errors', { errors: data.errors });
        console.warn('x-cart change', data.errors);
        return;
      }

      dclog('changeLine: dispatch CartUpdateEvent', {
        item_count: data.item_count,
        ...sectionKeysSummary(
          /** @type {Record<string, string | null | undefined>} */ (data.sections)
        ),
      });
      document.dispatchEvent(
        new CartUpdateEvent(data, this.#sectionId || 'x-cart', {
          itemCount: /** @type {number} */ (data.item_count ?? 0),
          source: 'x-cart',
          sections: /** @type {Record<string, string>} */ (data.sections),
        })
      );
    } catch (err) {
      if (/** @type {Error} */ (err).name !== 'AbortError') console.error(err);
    } finally {
      this.#popBusy();
    }
  }

  /**
   * @param {string} variantId
   * @param {HTMLElement} [trigger]
   */
  async #addVariant(variantId, trigger) {
    const ids = getXSectionIds();
    if (ids.length === 0) return;

    try {
      const cartCheckRes = await fetch('/cart.js');
      const currentCart = await readCartJsonResponse(cartCheckRes, 'cartLimitCheck');
      if (currentCart && typeof currentCart.total_price === 'number' && currentCart.total_price > CART_LIMIT_CENTS) {
        dclog('addVariant: cart limit reached', { total_price: currentCart.total_price });
        if (trigger) {
          const originalText = trigger.textContent;
          trigger.textContent = `Max ${CART_LIMIT_CENTS / 100}\u20AC`;
          setTimeout(() => { if (trigger) trigger.textContent = originalText; }, 2500);
        }
        return;
      }
    } catch {
      // Proceed if cart check fails
    }

    this.#pushBusy();
    if (trigger) trigger.setAttribute('aria-busy', 'true');

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');
    formData.append('sections', ids.join(','));
    formData.append('sections_url', xCartSectionsUrl());

    try {
      dclog('addVariant: request', {
        url: XRoutes.cart_add_url,
        variantId,
        sectionsRequested: ids,
        sections_url: xCartSectionsUrl(),
      });
      const res = await fetch(XRoutes.cart_add_url, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest', 'X-XCart-Internal': '1' },
        body: formData,
      });
      const data = await readCartJsonResponse(res, 'addVariant');
      if (!data) return;
      if (data.status) {
        dclog('addVariant: error status', { status: data.status, message: data.message });
        console.warn('x-cart add', data);
        return;
      }

      dclog('addVariant: dispatch CartUpdateEvent', {
        item_count: data.item_count,
        ...sectionKeysSummary(
          /** @type {Record<string, string | null | undefined>} */ (data.sections)
        ),
      });
      document.dispatchEvent(
        new CartUpdateEvent(data, this.#sectionId || 'x-cart', {
          itemCount: /** @type {number} */ (data.item_count ?? 0),
          source: 'x-cart',
          sections: /** @type {Record<string, string>} */ (data.sections),
        })
      );
    } catch (err) {
      console.error(err);
    } finally {
      if (trigger) trigger.removeAttribute('aria-busy');
      this.#popBusy();
    }
  }

  /**
   * @param {string} code
   * @param {HTMLElement} [trigger]
   */
  async #applyDiscount(code, trigger) {
    const ids = getXSectionIds();
    if (ids.length === 0) return;

    const errEl = this.querySelector('[data-x-promo-error]');
    errEl?.classList.add('hidden');

    this.#pushBusy();
    if (trigger) trigger.setAttribute('aria-busy', 'true');

    const body = JSON.stringify({
      discount: code,
      sections: ids.join(','),
      sections_url: xCartSectionsUrl(),
    });

    try {
      dclog('applyDiscount: request', {
        url: XRoutes.cart_update_url,
        sectionsRequested: ids,
        sections_url: xCartSectionsUrl(),
      });
      const res = await fetch(XRoutes.cart_update_url, {
        ...fetchConfig('json', { body }),
      });
      const data = await readCartJsonResponse(res, 'applyDiscount');
      if (!data) {
        dclog('applyDiscount: empty response', {});
        if (errEl instanceof HTMLElement) {
          errEl.textContent = this.dataset.i18nDiscountFailed || '';
          errEl.classList.remove('hidden');
        }
        return;
      }

      const badDiscount =
        Array.isArray(data.discount_codes) &&
        data.discount_codes.some(
          (/** @type {{ code: string; applicable: boolean }} */ d) =>
            d.code?.toLowerCase() === code.toLowerCase() && d.applicable === false
        );
      if (data.errors || data.status || badDiscount) {
        dclog('applyDiscount: rejected', { errors: data.errors, status: data.status, badDiscount });
        const notApplicable = this.dataset.i18nDiscountNotApplicable || '';
        const invalid = this.dataset.i18nDiscountInvalid || '';
        const msg =
          typeof data.errors === 'string'
            ? data.errors
            : /** @type {string} */ (data.message) ||
              (badDiscount ? notApplicable : invalid);
        if (errEl instanceof HTMLElement) {
          errEl.textContent = msg;
          errEl.classList.remove('hidden');
        }
        return;
      }

      dclog('applyDiscount: dispatch CartUpdateEvent', {
        item_count: data.item_count,
        ...sectionKeysSummary(
          /** @type {Record<string, string | null | undefined>} */ (data.sections)
        ),
      });
      document.dispatchEvent(
        new CartUpdateEvent(data, this.#sectionId || 'x-cart', {
          itemCount: /** @type {number} */ (data.item_count ?? 0),
          source: 'x-cart-discount',
          sections: /** @type {Record<string, string>} */ (data.sections),
        })
      );
    } catch (err) {
      console.error(err);
    } finally {
      if (trigger) trigger.removeAttribute('aria-busy');
      this.#popBusy();
    }
  }
  /**
   * @param {string} productUrl
   * @param {string} productTitle
   */
  async #openProductQuickAdd(productUrl, productTitle) {
    const modalContent = document.getElementById('quick-add-modal-content');
    const dialogEl = document.getElementById('quick-add-dialog');
    // Host theme has no Horizon quick-add dialog: degrade to the product page
    // so multi-variant upsells still reach a buyable surface.
    if (!modalContent || !dialogEl) {
      if (productUrl) window.location.href = productUrl;
      return;
    }

    this.#pickAbort?.abort();
    this.#pickAbort = new AbortController();
    this.#pushBusy();

    try {
      const res = await fetch(productUrl, { signal: this.#pickAbort.signal });
      if (!res.ok) return;

      const html = new DOMParser().parseFromString(await res.text(), 'text/html');
      const gridContent = html.querySelector('[data-product-grid-content]');
      if (!gridContent) return;

      if (window.innerWidth < 750) {
        const productDetails = gridContent.querySelector('.product-details');
        const productFormComponent = gridContent.querySelector('product-form-component');
        const variantPicker = gridContent.querySelector('variant-picker');
        const productPrice = gridContent.querySelector('product-price');

        const productHeader = document.createElement('div');
        productHeader.classList.add('product-header');
        const titleLink = document.createElement('a');
        titleLink.textContent = productTitle;
        titleLink.href = productUrl;
        productHeader.appendChild(titleLink);
        if (productPrice) productHeader.appendChild(productPrice);
        gridContent.appendChild(productHeader);
        if (variantPicker) gridContent.appendChild(variantPicker);
        if (productFormComponent) gridContent.appendChild(productFormComponent);
        productDetails?.remove();
      }

      morph(modalContent, gridContent);

      if (typeof /** @type {Record<string, unknown>} */ (dialogEl).showDialog === 'function') {
        /** @type {Record<string, unknown>} */ (dialogEl).showDialog();
      } else {
        dialogEl.querySelector('dialog')?.showModal();
      }
    } catch (err) {
      if (/** @type {Error} */ (err).name !== 'AbortError') console.error(err);
    } finally {
      this.#pickAbort = null;
      this.#popBusy();
    }
  }
}

dclog('module loaded', {
  cart_change_url: XRoutes.cart_change_url,
  cart_update_url: XRoutes.cart_update_url,
  cart_add_url: XRoutes.cart_add_url,
  xDomIds: getXSectionIds(),
});

if (!customElements.get('x-cart-root')) {
  customElements.define('x-cart-root', XCartRoot);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => scheduleXGiftSync(), { once: true });
} else {
  scheduleXGiftSync();
}

/* ------------------------------------------------------------------ *
 * External add-to-cart bridge.
 * Le Grand Rayon's own product form / quick-add adds to cart without
 * firing an x-cart event. We intercept cart AJAX (fetch + XHR) so the
 * drawer re-renders and (on add) auto-opens. Requests originating from
 * the x-cart itself carry the `X-XCart-Internal` header and are ignored.
 * ------------------------------------------------------------------ */
(function installXCartBridge() {
  function getDrawerRoot() {
    return (
      document.querySelector('x-cart-root[data-presentation="drawer"]') ||
      document.querySelector('x-cart-root')
    );
  }

  async function updateHeaderCount() {
    try {
      const cart = await fetch(XRoutes.cart_url + '.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
      if (cart && typeof cart.item_count === 'number') {
        document.querySelectorAll('.header__cart-count, [data-cart-count]').forEach((el) => {
          el.textContent = String(cart.item_count);
        });
      }
    } catch {
      /* ignore */
    }
  }

  let refreshTimer = /** @type {ReturnType<typeof setTimeout> | null} */ (null);
  async function refresh(openAfter) {
    try {
      await fetchXSectionsViaRenderingApi();
    } catch {
      /* ignore */
    }
    scheduleXGiftSync();
    void updateHeaderCount();
    if (openAfter) {
      const root = getDrawerRoot();
      if (root instanceof HTMLElement && root.dataset.autoOpen === 'true' && typeof (/** @type {any} */ (root).open) === 'function') {
        /** @type {any} */ (root).open();
      }
    }
  }
  function scheduleRefresh(openAfter) {
    if (refreshTimer) clearTimeout(refreshTimer);
    refreshTimer = setTimeout(() => {
      refreshTimer = null;
      void refresh(openAfter);
    }, 60);
  }

  function isInternal(headers) {
    if (!headers) return false;
    try {
      if (headers instanceof Headers) return headers.has('X-XCart-Internal');
      if (Array.isArray(headers)) return headers.some(([k]) => String(k).toLowerCase() === 'x-xcart-internal');
      return Object.keys(headers).some((k) => k.toLowerCase() === 'x-xcart-internal');
    } catch {
      return false;
    }
  }
  function classify(url) {
    const u = String(url || '');
    if (/\/cart\/add(\.js)?(\?|$)/.test(u)) return 'add';
    if (/\/cart\/(change|update|clear)(\.js)?(\?|$)/.test(u)) return 'change';
    return null;
  }

  const origFetch = window.fetch;
  if (typeof origFetch === 'function') {
    window.fetch = function (input, init) {
      const url = input instanceof Request ? input.url : input;
      const headers = (init && init.headers) || (input instanceof Request ? input.headers : null);
      const kind = classify(url);
      const internal = isInternal(headers);
      const p = origFetch.apply(this, arguments);
      if (kind && !internal && p && typeof p.then === 'function') {
        p.then((res) => {
          if (res && res.ok) scheduleRefresh(kind === 'add');
        }).catch(() => {});
      }
      return p;
    };
  }

  const XHR = window.XMLHttpRequest;
  if (XHR && XHR.prototype) {
    const origOpen = XHR.prototype.open;
    const origSend = XHR.prototype.send;
    const origSetHeader = XHR.prototype.setRequestHeader;
    XHR.prototype.open = function (method, url) {
      this.__xcartKind = classify(url);
      this.__xcartInternal = false;
      return origOpen.apply(this, arguments);
    };
    XHR.prototype.setRequestHeader = function (k, v) {
      if (String(k).toLowerCase() === 'x-xcart-internal') this.__xcartInternal = true;
      return origSetHeader.apply(this, arguments);
    };
    XHR.prototype.send = function () {
      if (this.__xcartKind && !this.__xcartInternal) {
        this.addEventListener('load', () => {
          if (this.status >= 200 && this.status < 300) scheduleRefresh(this.__xcartKind === 'add');
        });
      }
      return origSend.apply(this, arguments);
    };
  }

  /*
   * Own the add-to-cart submit. The host theme's product form posts to
   * /cart/add; if no other handler AJAX-ifies it the browser navigates away
   * and the drawer never opens. We AJAX the add ourselves (the fetch above
   * then drives open + refresh). `defaultPrevented` guard avoids double-adds
   * when another script already handles the submit.
   */
  document.addEventListener(
    'submit',
    (e) => {
      if (e.defaultPrevented) return;
      const target = /** @type {Element} */ (e.target);
      const form = target && typeof target.closest === 'function' ? target.closest('form') : null;
      if (!(form instanceof HTMLFormElement)) return;
      const action = form.getAttribute('action') || '';
      if (!/\/cart\/add/.test(action)) return;
      e.preventDefault();
      const submitter = /** @type {HTMLButtonElement | null} */ (/** @type {any} */ (e).submitter);
      if (submitter) submitter.setAttribute('aria-busy', 'true');
      const body = new FormData(form);
      origFetch(XRoutes.cart_add_url + '.js', {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body,
      })
        .then((r) => r.json().catch(() => ({})))
        .then(() => {
          // The fetch we just made is unmarked → the fetch interceptor would
          // normally fire, but we used origFetch to avoid the patch; drive the
          // drawer explicitly so behaviour is deterministic.
          scheduleRefresh(true);
        })
        .catch(() => {})
        .finally(() => {
          if (submitter) submitter.removeAttribute('aria-busy');
        });
    },
    false
  );
})();
